import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { contact, brand } from "@/data/siteContent";
import { gsap, prefersReducedMotion } from "@/lib/gsapSetup";
import useScrollReveal from "@/hooks/useScrollReveal";
import PrimaryCTA from "@/components/PrimaryCTA";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowRight, Check } from "lucide-react";

const EMPTY = { name: "", email: "", company: "", service: "", budget: "", message: "" };

const fieldInputCls =
  "w-full border-b border-white/15 bg-transparent pt-6 pb-2 text-white outline-none transition-colors focus:border-gold";

/** Accessible floating-label text field (input or textarea). */
function FloatingField({ id, label, value, onChange, type = "text", textarea = false, testid, autoComplete }) {
  const [focused, setFocused] = useState(false);
  const active = focused || (value && value.length > 0);
  const shared = {
    id,
    value,
    onChange,
    autoComplete,
    onFocus: () => setFocused(true),
    onBlur: () => setFocused(false),
    "data-testid": testid,
  };
  return (
    <div className={`field ${active ? "is-active" : ""}`}>
      <label htmlFor={id}>{label}</label>
      {textarea ? (
        <textarea rows={4} className={`${fieldInputCls} min-h-[120px] resize-none`} {...shared} />
      ) : (
        <input type={type} className={fieldInputCls} {...shared} />
      )}
    </div>
  );
}

/** Premium glass Select with an accessible persistent label + chevron. */
function LabeledSelect({ id, label, placeholder, value, onValueChange, options, testid }) {
  return (
    <div>
      <label id={`${id}-label`} className="mb-1 block text-xs uppercase tracking-[0.18em] text-white/55">
        {label}
      </label>
      <Select value={value || ""} onValueChange={onValueChange}>
        <SelectTrigger
          aria-labelledby={`${id}-label`}
          data-testid={testid}
          className="h-auto rounded-none border-0 border-b border-white/15 bg-transparent px-0 py-3 text-base text-white shadow-none ring-0 transition-colors data-[placeholder]:text-white/35 focus:border-gold focus:ring-0 [&>svg]:opacity-60"
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="glass-surface z-50 rounded-2xl border border-white/10 text-white">
          {options.map((o) => (
            <SelectItem
              key={o}
              value={o}
              data-testid={`${testid}-option-${o}`}
              className="cursor-pointer rounded-lg py-2.5 text-white focus:bg-gold/15 focus:text-white data-[state=checked]:text-gold"
            >
              {o}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function buildMailto({ name, email, company, service, budget, message }) {
  const subject = encodeURIComponent(
    service ? `Project inquiry — ${service}` : "Project inquiry"
  );
  const body = encodeURIComponent(
    [
      message,
      "",
      "—",
      `Name: ${name}`,
      `Email: ${email}`,
      company ? `Company: ${company}` : null,
      service ? `Service: ${service}` : null,
      budget ? `Budget: ${budget}` : null,
    ]
      .filter(Boolean)
      .join("\n")
  );
  return `mailto:${brand.email}?subject=${subject}&body=${body}`;
}

export default function ContactCTA() {
  const ref = useScrollReveal({ y: 30, stagger: 0.06 });
  const [form, setForm] = useState(EMPTY);
  const [done, setDone] = useState(false);
  const formRef = useRef(null);

  // Soft ambient light — transform/opacity only; skipped under reduced motion (static blobs remain).
  useEffect(() => {
    if (prefersReducedMotion()) return undefined;
    const scope = ref.current;
    if (!scope) return undefined;

    const ctx = gsap.context(() => {
      const a = scope.querySelector('[data-contact-blob="a"]');
      const b = scope.querySelector('[data-contact-blob="b"]');
      const c = scope.querySelector('[data-contact-blob="c"]');
      if (!a || !b || !c) return;

      gsap.to(a, {
        x: 48,
        y: -36,
        scale: 1.06,
        duration: 18,
        ease: "sine.inOut",
        yoyo: true,
        repeat: -1,
      });
      gsap.to(b, {
        x: -56,
        y: 40,
        scale: 1.08,
        duration: 22,
        ease: "sine.inOut",
        yoyo: true,
        repeat: -1,
      });
      gsap.to(c, {
        x: 28,
        y: 24,
        opacity: 0.18,
        duration: 14,
        ease: "sine.inOut",
        yoyo: true,
        repeat: -1,
      });
    }, scope);

    return () => ctx.revert();
  }, [ref]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setField = (k, val) => setForm((f) => ({ ...f, [k]: val }));

  const validate = () => {
    if (!form.name.trim()) return "Please tell us your name.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) return "Please enter a valid email.";
    if (!form.message.trim()) return "Please add a short message.";
    return null;
  };

  const submit = (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      company: form.company.trim(),
      service: form.service,
      budget: form.budget,
      message: form.message.trim(),
    };
    // No backend for now — open the visitor's mail client with a prefilled message.
    window.location.href = buildMailto(payload);
    setDone(true);
    setForm(EMPTY);
  };

  return (
    <section
      id="contact"
      ref={ref}
      className="relative overflow-hidden bg-[#050505]"
      data-testid="contact-section"
    >
      {/* Ambient atmosphere — sits behind copy + form */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div
          data-contact-blob="a"
          className="absolute -left-[12%] top-[8%] h-[58vmin] w-[58vmin] rounded-full opacity-[0.45] will-change-transform"
          style={{
            background:
              "radial-gradient(circle, rgba(201,165,94,0.16) 0%, transparent 68%)",
          }}
        />
        <div
          data-contact-blob="b"
          className="absolute -right-[8%] bottom-[0%] h-[52vmin] w-[52vmin] rounded-full opacity-[0.35] will-change-transform"
          style={{
            background:
              "radial-gradient(circle, rgba(255,255,255,0.07) 0%, transparent 70%)",
          }}
        />
        <div
          data-contact-blob="c"
          className="absolute left-[38%] top-[42%] h-[42vmin] w-[42vmin] rounded-full opacity-[0.28] will-change-transform"
          style={{
            background:
              "radial-gradient(circle, rgba(201,165,94,0.1) 0%, transparent 65%)",
          }}
        />
      </div>

      <div className="relative z-10 mx-auto grid max-w-[1400px] grid-cols-1 gap-16 px-6 py-20 md:px-10 md:py-32 lg:grid-cols-2 lg:gap-24">
        {/* Left */}
        <div className="flex flex-col justify-between">
          <div>
            <p
              data-reveal
              className="mb-6 font-accent text-lg italic text-white/45"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {contact.eyebrow}
            </p>
            <h2
              data-reveal
              className="font-display text-5xl leading-[1] tracking-tight text-white sm:text-6xl lg:text-7xl"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              {contact.heading}
            </h2>
            <p data-reveal className="mt-8 max-w-md font-body text-base leading-relaxed text-white/70 md:text-lg">
              {contact.subcopy}
            </p>
          </div>

          <div data-reveal className="mt-12 space-y-2">
            <a
              href={`mailto:${brand.email}`}
              className="block text-lg text-white transition-colors hover:text-gold"
              data-testid="contact-email"
            >
              {brand.email}
            </a>
            <a
              href={`tel:${brand.phoneTel || brand.phone}`}
              className="block text-white/65 transition-colors hover:text-gold"
              data-testid="contact-phone"
            >
              {brand.phone}
            </a>
            <a
              href={brand.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-white/65 transition-colors hover:text-gold"
              data-testid="contact-whatsapp"
            >
              WhatsApp
            </a>
            {brand.website ? (
              <a
                href={brand.website}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-white/65 transition-colors hover:text-gold"
                data-testid="contact-website"
              >
                {brand.websiteLabel || brand.website}
              </a>
            ) : null}
            <div className="pt-1 text-white/55">{brand.location}</div>
          </div>
        </div>

        {/* Right — form */}
        <div data-reveal>
          {done ? (
            <div
              className="glass-surface flex h-full min-h-[360px] flex-col items-start justify-center rounded-[1.5rem] border border-white/10 p-10"
              data-testid="contact-success"
            >
              <span className="mb-6 flex h-14 w-14 items-center justify-center rounded-full border border-white/20">
                <Check className="text-white" />
              </span>
              <h3
                className="font-display text-3xl text-white"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              >
                Open your email app to send.
              </h3>
              <p className="mt-3 max-w-sm text-white/70">
                We prefilled a message to {brand.email}. Send it when you’re ready — or email us
                directly anytime.
              </p>
              <button
                onClick={() => setDone(false)}
                className="mt-8 text-sm text-white/70 underline underline-offset-4 hover:text-white"
                data-testid="contact-reset"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form
              ref={formRef}
              onSubmit={submit}
              className="glass-surface relative flex flex-col gap-7 rounded-[1.5rem] border border-white/10 p-8 md:p-10"
              data-testid="contact-form"
              noValidate
            >
              <div className="grid grid-cols-1 gap-7 sm:grid-cols-2">
                <FloatingField
                  id="contact-name"
                  label="Full name"
                  value={form.name}
                  onChange={update("name")}
                  autoComplete="name"
                  testid="contact-input-name"
                />
                <FloatingField
                  id="contact-email"
                  label="Email address"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  autoComplete="email"
                  testid="contact-input-email"
                />
              </div>

              <FloatingField
                id="contact-company"
                label="Company (optional)"
                value={form.company}
                onChange={update("company")}
                autoComplete="organization"
                testid="contact-input-company"
              />

              <div className="grid grid-cols-1 gap-7 sm:grid-cols-2">
                <LabeledSelect
                  id="contact-service"
                  label="Service of interest"
                  placeholder="Select a service"
                  value={form.service}
                  onValueChange={(v) => setField("service", v)}
                  options={contact.services}
                  testid="contact-select-service"
                />
                <LabeledSelect
                  id="contact-budget"
                  label="Budget range"
                  placeholder="Select a range"
                  value={form.budget}
                  onValueChange={(v) => setField("budget", v)}
                  options={contact.budgets}
                  testid="contact-select-budget"
                />
              </div>

              <FloatingField
                id="contact-message"
                label="Tell us about your project"
                textarea
                value={form.message}
                onChange={update("message")}
                testid="contact-input-message"
              />

              <PrimaryCTA
                as="button"
                type="submit"
                className="mt-2 w-fit px-8 py-4"
                data-testid="contact-submit-button"
              >
                {contact.ctaLabel || "Tell Us What You’re Building"}
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </PrimaryCTA>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
